import { useState, useEffect } from 'react';
import { useGameStore } from '@/store/gameStore';

/**
 * Hook that enables keyboard game controls when on the game screen.
 * Returns the currently focused node index for rendering a focus indicator.
 *
 * Controls:
 * - Tab / Shift+Tab: cycle focus through nodes
 * - Enter / Space: strike the focused node
 * - Escape: undo last strike
 * - R: reset puzzle
 * - Backspace: return to puzzle select
 */
export function useKeyboardNavigation(): number {
  const board = useGameStore((s) => s.board);
  const strikeNode = useGameStore((s) => s.strikeNode);
  const undo = useGameStore((s) => s.undo);
  const reset = useGameStore((s) => s.reset);
  const screen = useGameStore((s) => s.screen);
  const navigateTo = useGameStore((s) => s.navigateTo);

  const [focusedNodeIndex, setFocusedNodeIndex] = useState(-1);

  // Reset focus when leaving game screen or when board changes
  useEffect(() => {
    if (screen !== 'game') {
      setFocusedNodeIndex(-1);
    }
  }, [screen, board]);

  useEffect(() => {
    if (screen !== 'game') return;

    const handler = (e: KeyboardEvent) => {
      const currentBoard = useGameStore.getState().board;
      if (!currentBoard) return;
      const nodes = currentBoard.nodes;

      switch (e.key) {
        case 'Tab':
          e.preventDefault();
          if (e.shiftKey) {
            setFocusedNodeIndex((i) => (i <= 0 ? nodes.length - 1 : i - 1));
          } else {
            setFocusedNodeIndex((i) =>
              i >= nodes.length - 1 ? 0 : i + 1,
            );
          }
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          setFocusedNodeIndex((currentIndex) => {
            if (currentIndex >= 0 && currentIndex < nodes.length) {
              const node = nodes[currentIndex];
              if (node) {
                strikeNode(node.id);
              }
            }
            return currentIndex;
          });
          break;
        case 'Escape':
          e.preventDefault();
          undo();
          break;
        case 'r':
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            reset();
          }
          break;
        case 'Backspace':
          e.preventDefault();
          navigateTo('puzzleSelect');
          break;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [screen, strikeNode, undo, reset, navigateTo]);

  return focusedNodeIndex;
}
